import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useEffect, useState, useMemo, useCallback } from "react";
import { IndustrialEquipment } from "../../api-models/IndustrialEquipment";

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  filterControl: {
    minWidth: 200,
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  resultCount: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  // Override parent styles for dropdown menu
  menuPaper: {
    maxHeight: "300px !important",
  },
  menuList: {
    // Force vertical layout in case parent has flex-row
    "&.MuiList-root": {
      display: "flex",
      flexDirection: "column",
      padding: "8px 0",
    },
    "& .MuiMenuItem-root": {
      whiteSpace: "normal",
      wordWrap: "break-word",
      textAlign: "left",
      justifyContent: "flex-start",
      padding: "6px 16px",
    },
  },
}));

interface ProductFiltersProps {
  equipment: IndustrialEquipment[];
  onFilteredDataChange: (filtered: IndustrialEquipment[]) => void;
  preselectedClass?: string;
}

export const ProductFilters = ({
  equipment,
  onFilteredDataChange,
  preselectedClass,
}: ProductFiltersProps) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] =
    useState<string>("all");
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Debounce search term to avoid filtering on every keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Memoize unique values extraction to avoid recomputing on every render
  const { manufacturers, classList, statuses } = useMemo(() => {
    const uniqueManufacturers = Array.from(
      new Set(equipment.map((item) => item.manufacturerName).filter(Boolean))
    ).sort();

    const uniqueClasses = Array.from(
      new Set(
        equipment.map((item) => item.requirementsClass?.name).filter(Boolean)
      )
    ).sort();

    const uniqueStatuses = Array.from(
      new Set(equipment.map((item) => item.status).filter(Boolean))
    ).sort();

    return {
      manufacturers: uniqueManufacturers,
      classList: uniqueClasses,
      statuses: uniqueStatuses,
    };
  }, [equipment]);

  // Set preselected class from fuzzy search
  useEffect(() => {
    if (preselectedClass) {
      setSelectedClass(preselectedClass);
    }
  }, [preselectedClass]);

  // Memoized filter function
  const applyFilters = useCallback(
    (
      search: string,
      manufacturer: string,
      equipmentClass: string,
      status: string
    ) => {
      let filtered = [...equipment];

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.requirementsClass.name.toLowerCase().includes(searchLower) ||
            item.eqhubProductId.toString().includes(searchLower) ||
            item.partNumber.toLowerCase().includes(searchLower) ||
            item.modelNumber.toLowerCase().includes(searchLower) ||
            item.manufacturerName.toLowerCase().includes(searchLower)
        );
      }

      if (manufacturer !== "all") {
        filtered = filtered.filter(
          (item) => item.manufacturerName === manufacturer
        );
      }

      if (equipmentClass !== "all") {
        filtered = filtered.filter(
          (item) => item.requirementsClass?.name === equipmentClass
        );
      }

      if (status !== "all") {
        filtered = filtered.filter((item) => item.status === status);
      }

      onFilteredDataChange(filtered);
    },
    [equipment, onFilteredDataChange]
  );

  // Apply filters when debounced search or other filters change
  useEffect(() => {
    applyFilters(
      debouncedSearchTerm,
      selectedManufacturer,
      selectedClass,
      selectedStatus
    );
  }, [
    debouncedSearchTerm,
    selectedManufacturer,
    selectedClass,
    selectedStatus,
    applyFilters,
  ]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleManufacturerChange = (value: string) => {
    setSelectedManufacturer(value);
  };

  const handleClassChange = (value: string) => {
    setSelectedClass(value);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box className={classes.filtersContainer}>
      <Typography variant="h6" className={classes.title}>
        Filter Equipment
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name, ID, part..."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.filterControl}
          >
            <InputLabel>Manufacturer</InputLabel>
            <Select
              value={selectedManufacturer}
              onChange={(e) =>
                handleManufacturerChange(e.target.value as string)
              }
              label="Manufacturer"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
                PaperProps: {
                  className: classes.menuPaper,
                },
                MenuListProps: {
                  className: classes.menuList,
                },
              }}
            >
              <MenuItem value="all">All Manufacturers</MenuItem>
              {manufacturers.map((manufacturer) => (
                <MenuItem key={manufacturer} value={manufacturer}>
                  {manufacturer}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.filterControl}
          >
            <InputLabel>Class</InputLabel>
            <Select
              value={selectedClass}
              onChange={(e) => handleClassChange(e.target.value as string)}
              label="Class"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
                PaperProps: {
                  className: classes.menuPaper,
                },
                MenuListProps: {
                  className: classes.menuList,
                },
              }}
            >
              <MenuItem value="all">All Classes</MenuItem>
              {classList.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.filterControl}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value as string)}
              label="Status"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
                PaperProps: {
                  className: classes.menuPaper,
                },
                MenuListProps: {
                  className: classes.menuList,
                },
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {formatStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};
